import { Module } from "vuex";
import { AutonomousFormAdditionalFieldService } from "../../services/AutonomousFormAdditionalFieldService";
import { RootState } from "../RootState";
import { FlowBaseFieldUpdateRequest } from "../../models/requests/FlowBaseFieldUpdateRequest";
import { AutonomousFormAdditionalField } from "../../models/responses/AutonomousFormAdditionalField";
import {
  FlowAdditionalFieldCreateRequest,
  FlowAdditionalFieldCreateRequestImpl
} from "../../models/requests/FlowAdditionalFieldCreateRequest";

export interface AutonomousFormAdditionalFieldState {
  additionalFields: AutonomousFormAdditionalField[];
}

// Magasin VueX qui coordonne l'échange d'infos entre les différentes vues.
export const autonomousFormAdditionalFieldModule: Module<
  AutonomousFormAdditionalFieldState,
  RootState
> = {
  namespaced: true,
  // Données partagées du magasin (ne peuvent pas être modifiées directement)
  state: {
    additionalFields: []
  },

  // Accesseurs pour propriétés calculées
  getters: {
    orders(state) {
      return state.additionalFields.map(field => field.order);
    },
    maxOrder(state, getters) {
      return getters.orders.reduce(
        (a: number, b: number) => Math.max(a, b),
        -1
      );
    }
  },

  // Opérations de modification des données
  mutations: {
    /**
     * Updates autonomousFormBaseFields
     */
    setAdditionalFields(state, value: AutonomousFormAdditionalField[]) {
      state.additionalFields = value;
    },
    /**
     * Deletes an additional field
     * @param state
     * @param fieldId
     */
    deleteAdditionalField(state, fieldId: number) {
      const index = state.additionalFields.findIndex(val => val.id === fieldId);
      if (index > -1) {
        state.additionalFields.splice(index, 1);
      }
    },

    /**
     * Adds or update an additional field
     * @param state
     * @param value
     */
    addOrUpdateAdditionalField(state, value: AutonomousFormAdditionalField) {
      const index = state.additionalFields.findIndex(
        val => val.id === value.id
      );
      if (index > -1) {
        state.additionalFields.splice(index, 1, value);
      } else {
        state.additionalFields.push(value);
      }
    }
  },

  // Actions pouvant être appelées par les vues pour agir sur le magasin
  actions: {
    async loadField({ commit }, value: { fieldId: number; flowId: number }) {
      const response = await AutonomousFormAdditionalFieldService.getField(
        value.fieldId,
        value.flowId
      );
      commit("addOrUpdateAdditionalField", response.data);
      return response.data;
    },

    async deleteField({ commit }, value: { fieldId: number; flowId: number }) {
      const response = await AutonomousFormAdditionalFieldService.deleteField(
        value.fieldId,
        value.flowId
      );
      commit("deleteAdditionalField", response.data?.id);
      return response.data;
    },

    async setField(
      context,
      value: {
        fieldId: number;
        flowId: number;
        field: FlowBaseFieldUpdateRequest;
      }
    ) {
      const response = await AutonomousFormAdditionalFieldService.setField(
        value.fieldId,
        value.flowId,
        value.field
      );
      context.commit("addOrUpdateAdditionalField", response.data);
      return response.data;
    },

    async createField(
      context,
      value: { flowId: number; field: FlowAdditionalFieldCreateRequest }
    ) {
      const response = await AutonomousFormAdditionalFieldService.createField(
        value.flowId,
        value.field
      );
      context.commit("addOrUpdateAdditionalField", response.data);
    },

    async createDefaultField(context, flowId: number) {
      const field = new FlowAdditionalFieldCreateRequestImpl(
        flowId,
        context.getters.maxOrder + 1
      );
      const response = await AutonomousFormAdditionalFieldService.createField(
        flowId,
        field
      );
      context.commit("addOrUpdateAdditionalField", response.data);
    },

    async loadAllFields(context, flowId: number) {
      const results = await AutonomousFormAdditionalFieldService.getFields(
        flowId
      );

      context.commit("setAdditionalFields", results.data);
      return results;
    }
  }
};
